import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { get } from '@/features/shared/lib/api';
import { arr, getLoginUrlWithRedirect } from '@/features/shared/lib/utils';
import { useMetricsWidgets } from '@/features/shared/lib/metrics-widgets';
import { CollaborationChart } from '@/features/journal/components/charts/CollaborationChart';
import { ImpactFactorTrendChart } from '@/features/journal/components/charts/ImpactFactorTrendChart';
import { YearlyPublicationsChart } from '@/features/journal/components/charts/YearlyPublicationsChart';
import JournalCitationMetrics from '@/features/journal/components/JournalCitationMetrics';
import JournalIndexingInfo from '@/features/journal/components/JournalIndexingInfo';
import CategoryRankingTable from '@/features/journal/components/CategoryRankingTable';
import { MajorPublishingDepartments } from '@/features/journal/components/charts/MajorPublishingDepartments';
import PublicationCard from "@/features/publication/components/PublicationCard";
import JournalMajorAuthors from '@/features/journal/components/JournalMajorAuthors';
import JournalRightBox from '@/features/journal/components/JournalRightBox';
import PageBreadcrumb from '@/features/shared/components/PageBreadcrumb';
import AimsAndScope from '@/features/journal/components/AimsAndScope';

export default function Journal() {
  const { id: pid } = useParams();      // /journals/:id 지원
  const [sp] = useSearchParams();       // /journals/?id=... 도 계속 지원
  const id = pid || sp.get('id');

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Indicator (moved here so other sections can use it)
  const [indicator, setIndicator] = useState(null);
  const [indicatorLoading, setIndicatorLoading] = useState(false);
  const [indicatorErr, setIndicatorErr] = useState(null);

  // Indexing / metrics update dates (JCR / Scopus / KCI)
  const [indexDates, setIndexDates] = useState(null);
  const [indexDatesLoading, setIndexDatesLoading] = useState(false);
  const [indexDatesErr, setIndexDatesErr] = useState(null);

  // Publications by PNU Researchers
  const [pubRows, setPubRows] = useState([]);
  const [pubLoading, setPubLoading] = useState(false);
  const [pubErr, setPubErr] = useState(null);

  // SciVal (Journal infographics) - single fetch for 3 charts
  const [scivalMetrics, setScivalMetrics] = useState(null);
  const [scivalLoading, setScivalLoading] = useState(false);
  const [scivalErr, setScivalErr] = useState(null);

  const hasScopusSourceId = useMemo(() => {
    return !!(record?.scopus_id && String(record.scopus_id).trim());
  }, [record?.scopus_id]);

  // sourceId가 있어도 SciVal API가 status:false(예: 403)면 섹션 자체를 숨김
  // 단, 로딩 중에는 스켈레톤을 보여주기 위해 섹션을 유지
  const showScivalSection = hasScopusSourceId && (scivalLoading || (!!scivalMetrics && !scivalErr));

  useEffect(() => {
    if (!id) return;
    let alive = true;

    async function run() {
      setLoading(true);
      setErr(null);
      try {
        const data = await get(`/rm/v1/journal/${encodeURIComponent(id)}`);
        if (alive) {
          const rec = data?.result?.record || null;
          setRecord(rec);
        }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.message || e.message);
      } finally {
        if (alive) setLoading(false);
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, [id]);

  useEffect(() => {
    const jt = record?.journal_title;
    if (!jt) return;
    const site =
      typeof rmVars !== 'undefined' && rmVars.siteName
        ? String(rmVars.siteName).trim()
        : '';
    document.title = site ? `${jt} - Journal - ${site}` : jt;
  }, [record?.journal_title]);

  // Fetch SciVal metrics once (based on Scopus source id in journal record)
  useEffect(() => {
    const sourceId = record?.scopus_id ? String(record.scopus_id).trim() : '';
    if (!sourceId) return;
    let alive = true;

    async function run() {
      setScivalLoading(true);
      setScivalErr(null);
      try {
        const res = await get('/rm/v1/scival/journal_metrics', { params: { sourceId, yearRange: '10yrs' } });
        if (!alive) return;
        if (res?.status && res?.result) {
          setScivalMetrics(res.result);
        } else {
          setScivalMetrics(null);
          setScivalErr(res?.message || 'SciVal 데이터를 가져오는데 실패했습니다.');
        }
      } catch (e) {
        if (!alive) return;
        setScivalMetrics(null);
        setScivalErr(e?.response?.data?.message || e.message);
      } finally {
        if (alive) setScivalLoading(false);
      }
    }

    run();
    return () => { alive = false; };
  }, [record?.scopus_id]);

  // Fetch indicator data (moved from component to route for reuse)
  useEffect(() => {
    if (!id) return;
    let alive = true;
    async function run() {
      setIndicatorLoading(true);
      setIndicatorErr(null);
      try {
        const rec = await fetchRemote(id);
        if (alive) setIndicator(rec);
      } catch (e) {
        if (alive) setIndicatorErr(e?.response?.data?.message || e.message);
      } finally {
        if (alive) setIndicatorLoading(false);
      }
    }
    run();
    return () => { alive = false; };
  }, [id]);

  // Fetch indexing / metrics update dates (global)
  useEffect(() => {
    let alive = true;
    async function run() {
      setIndexDatesLoading(true);
      setIndexDatesErr(null);
      try {
        const res = await get('/rm/v1/journal/indexing_update_dates');
        if (!alive) return;
        setIndexDates(res?.result || null);
      } catch (e) {
        if (alive) setIndexDatesErr(e?.response?.data?.message || e.message);
      } finally {
        if (alive) setIndexDatesLoading(false);
      }
    }
    run();
    return () => { alive = false; };
  }, []);

  // Compute Impact Factor series from indicator
  const impactSeries = useMemo(() => {
    const graph = indicator?.impact_factor_graph;
    if (!graph) return [];
    const mapSeries = (label, color, arr) => {
      const rows = Array.isArray(arr) ? arr : [];
      const data = rows
        .filter(d => d && typeof d.indicator_year === 'number' && d.indicator_year > 0 && typeof d.impact !== 'undefined' && d.impact !== null && !isNaN(Number(d.impact)))
        .map(d => ({ year: d.indicator_year, value: Number(d.impact) }))
        .sort((a, b) => a.year - b.year);
      if (!data.length) return null;
      return { label, color, data };
    };
    const sci = mapSeries('SCI', '#4F55FF', graph.SCI);
    const scopus = mapSeries('Scopus', '#F26B2A', graph.Scopus);
    const kci = mapSeries('KCI', '#0B5EA8', graph.KCI);
    return [sci, scopus, kci].filter(Boolean);
  }, [indicator]);

  // Fetch publications by PNU researchers for this journal
  useEffect(() => {
    if (!id) return;
    let alive = true;

    async function run() {
      setPubLoading(true);
      setPubErr(null);
      try {
        const params = {
          'journal_id[]': [id], order_by: 'date', order: 'desc', record_per_page: 5, current_page: 1,
        };
        const data = await get('/rm/v1/publications', { params });
        const rows = Array.isArray(data?.result?.records) ? data.result.records : [];
        if (alive) setPubRows(rows);
      } catch (e) {
        if (alive) setPubErr(e?.response?.data?.message || e.message);
      } finally {
        if (alive) setPubLoading(false);
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, [id]);

  useMetricsWidgets({
    enabled: !pubLoading && Array.isArray(pubRows) && pubRows.length > 0,
    delaysMs: { altmetric: 1000, plumx: 1000 },
    cleanupBeforeLoad: true,
    plumxRemoveRenderedDom: true,
    plumxRenderedSelector: '.plx-wrapping-print-link',
    plumxWireUpDelayMs: 150,
  });

  // Altmetric 스크립트 삭제 유틸
  const removeAltmetricScript = () => {
    // 스크립트 태그 삭제
    const existingScript = document.querySelector('script[src*="d1bxh8uas1mnw7.cloudfront.net/assets/embed.js"]');
    if (existingScript && existingScript.parentNode) {
      existingScript.parentNode.removeChild(existingScript);
    }
    // window.altmetric 초기화
    if (typeof window !== 'undefined' && window.altmetric) {
      try {
        delete window.altmetric;
      } catch (e) {
        // ignore
      }
    }
    // altmetric-embed-js 태그도 삭제 (embed.js가 생성한 태그)
    const embedScript = document.getElementById('altmetric-embed-js');
    if (embedScript && embedScript.parentNode) {
      embedScript.parentNode.removeChild(embedScript);
    }
  };

  // Altmetric 스크립트 로드 유틸
  const loadAltmetricScript = () => {
    return new Promise((resolve, reject) => {
      // 이미 로드되어 있는지 확인
      const existingScript = document.querySelector('script[src*="d1bxh8uas1mnw7.cloudfront.net/assets/embed.js"]');
      if (existingScript) {
        // 이미 로드되어 있고 window.altmetric가 준비되었는지 확인
        const checkAltmetric = (retries = 10) => {
          if (typeof window !== 'undefined' && window.altmetric && typeof window.altmetric.process === 'function') {
            resolve();
            return;
          }
          if (retries > 0) {
            setTimeout(() => checkAltmetric(retries - 1), 200);
          } else {
            resolve(); // 타임아웃되어도 resolve (재시도는 초기화 로직에서)
          }
        };
        checkAltmetric();
        return;
      }

      // 스크립트 동적 로드
      const script = document.createElement('script');
      script.src = 'https://d1bxh8uas1mnw7.cloudfront.net/assets/embed.js';
      script.async = true;
      document.body.appendChild(script);

      const script2 = document.createElement('script');
      script2.src = 'https://cdn.plu.mx/widget-popup.js';
      script2.async = true;
      document.body.appendChild(script2);
    });
  };

  // API 데이터 로드 완료 후 Altmetric 스크립트 로드 및 초기화
  useEffect(() => {
    // 데이터가 없거나 로딩 중이면 대기
    if (!pubRows || pubRows.length === 0 || pubLoading) return;

    let isMounted = true;

    // 스크립트 로드 후 초기화
    loadAltmetricScript().then(() => {
      if (!isMounted) return;

      const initAltmetric = () => {
        // 스크립트와 window.altmetric 확인
        if (typeof window === 'undefined' || !window.altmetric || typeof window.altmetric.process !== 'function') {
          return false;
        }

        // DOM에 실제로 요소가 렌더링되었는지 확인
        const elements = document.querySelectorAll('.altmetric-embed');
        if (elements.length === 0) {
          return false;
        }

        let processed = 0;
        elements.forEach((el) => {
          try {
            // 기존 처리 플래그 제거 (재처리를 위해)
            delete el.dataset.altmetricProcessed;
            window.altmetric.process(el);
            processed++;
          } catch (e) {
            console.error('[Journal] Altmetric process error:', e);
          }
        });

        return processed > 0;
      };

      // requestAnimationFrame을 사용하여 DOM 렌더링 완료 후 초기화
      const tryInitWithDelay = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!isMounted) return;
            // DOM 렌더링 완료 후 약간의 지연을 두고 초기화
            setTimeout(() => {
              if (!isMounted) return;
              if (!initAltmetric()) {
                // 첫 시도 실패시 재시도
                setTimeout(() => {
                  if (!isMounted) return;
                  if (!initAltmetric()) {
                    // 두 번째 시도 실패시 한 번 더 재시도
                    setTimeout(() => {
                      if (!isMounted) return;
                      initAltmetric();
                    }, 800);
                  }
                }, 500);
              }
            }, 200);
          });
        });
      };

      tryInitWithDelay();
    }).catch((e) => {
      console.error('[Journal] Failed to load Altmetric script:', e);
    });

    return () => {
      isMounted = false;
    };
  }, [pubRows, pubLoading]);

  // id가 변경될 때 (다른 저널 페이지로 이동 시) 스크립트 삭제
  useEffect(() => {
    return () => {
      // 컴포넌트 언마운트 또는 id 변경 시 스크립트 삭제
      removeAltmetricScript();
    };
  }, [id]);

  // Fetch function moved from JournalCitationMetrics.jsx
  async function fetchRemote(journalId) {
    const res = await get(`/rm/v1/journal/${encodeURIComponent(journalId)}/indicator`);
    return res?.result?.record || null;
  }

  const navigate = useNavigate();
  const location = useLocation();
  const handleBack = (e) => {
    try { if (e && typeof e.preventDefault === 'function') e.preventDefault(); } catch (err) { }
    try {
      if (typeof window !== 'undefined' && window.history && window.history.length > 1) {
        window.history.back();
      } else {
        navigate('/journals');
      }
    } catch (err) {
      navigate('/journals');
    }
  };

  const handleMoreResources = () => {
    if (!record?.journal_id) return;
    navigate(`/publications/?journal_id[]=${encodeURIComponent(record.journal_id)}`);
    // 스크롤을 상단으로 이동
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, 0);
  };

  const showAltmetric = !pubLoading && Array.isArray(pubRows) && pubRows.length > 0;

  if (!id) return <div className="p-6">잘못된 접근입니다.</div>;
  if (err) return <div className="p-6 text-red-600">에러: {String(err)}</div>;
  if (loading && !record) return <div className="p-6">Loading...</div>;
  // return <div className="p-6">Detail for {id}</div>;

  return (
    <div className="flexPage mt-[127px] lg:mt-[120px] px-6 2xl:px-[100px]">
      <div className="w-full pt-[24px] lg:relative lg:w-[calc(100%-360px)] lg:mt-[105px] lg:bg-white lg:rounded-xl lg:px-[60px] lg:py-[60px] lg:pt-[60px]">
        <a href="#" onClick={handleBack} className="not_print inline-flex items-center gap-[6px] bg-[#F3F5F9] mb-6 py-[10px] pl-[10px] pr-[16px] rounded-md lg:pl-0 lg:pr-0 lg:py-0 lg:bg-transparent lg:absolute lg:-top-[44px] lg:left-0">
          <img src={`${rmVars.themeUrl}images/chevron_right_8D9AAF.png`} className="w-3 rotate-180" alt="back" />
          <span className="text-[14px] text-[#6C7687] font-medium leading-[100%] lg:text-[16px] xxl:text-red-500">
            Back <span className="hidden lg:inline"> to Journal list</span>
          </span>
        </a>

        <PageBreadcrumb
          current={record?.journal_title || ''}
          parent="Journals"
          parentTo="/journals"
          hideOnMobile
        />

        <div className="tit-area w-full flex justify-between items-center gap-[6px] mb-5 lg:mb-6">
          <h2 className="text-[22px] leading-[130%] font-black lg:text-[26px]">
            {record?.journal_title || ''}
            {typeof rmVars !== 'undefined' && rmVars?.isEditor && id && (
              <a
                href={`${rmVars.homeUrl}/wp-admin/admin.php?page=journal&record_id=${id}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center justify-center w-[18px] h-[18px] shrink-0 ml-2"
                target="_blank"
                rel="noopener noreferrer"
                title="Edit Journal"
              >
                <span
                  className="editor-edit"
                  style={{
                    width: '18px',
                    height: '18px',
                    filter: 'invert(11%) sepia(70%) saturate(6625%) hue-rotate(348deg) brightness(88%) contrast(99%)',
                    backgroundSize: '18px auto',
                    display: 'inline-block',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center center',
                    backgroundImage: `url(${rmVars.themeUrl}images/edit-regular.svg)`
                  }}
                />
              </a>
            )}
          </h2>
          <button className="btn-print flex-shrink-0 hidden px-3 py-[6px] items-center gap-[6px] bg-[#E4E8F1] rounded-[8px] cursor-pointer lg:flex"
            onClick={() => window.print()}>
            <img src={`${rmVars.themeUrl}images/print-ico.png`} className="w-[24px] h-[24px]" alt="filter" />
            <span className="text-[14px] text-[#6C7687] font-semibold leading-[100%]">
              Print
            </span>
          </button>
        </div>

        {(() => {
          const translationTitle = Array.isArray(record?.journal_titles)
            ? record.journal_titles.find(title => title.type === "번역 서명")?.title
            : null;

          return translationTitle ? (
            <div className="text-[14px] font-semibold text-[#8D9AAF] leading-[140%]" >
              {translationTitle}
            </div>
          ) : null;
        })()}

        <div className="journaldetailPage detailPage">
          <div className="not_print pb-6 text-[14px] font-semibold text-[#8D9AAF] leading-[140%] lg:pb-[30px]">
          </div>

          <div className="issn flex flex-col gap-y-[14px] py-6 text-[14px] leading-[100%] font-semibold border-y border-y-[#E4E8F1] lg:flex-row gap-x-5 lg:py-4">
            {(() => {
              const info = [];
              const issn = record?.issn && String(record.issn).trim();
              const eissn = record?.eissn && String(record.eissn).trim();
              const publisher = record?.publisher && String(record.publisher).trim();
              if (issn) info.push({ label: 'ISSN', value: issn });
              if (eissn) info.push({ label: 'eISSN', value: eissn });
              if (publisher) info.push({ label: 'Publisher', value: publisher });
              if (!info.length) return null;
              return info.map((it, idx) => (<React.Fragment key={it.label}>
                <div className={`${it.label !== 'Publisher' ? 'issn_num ' : ''}flex items-center gap-x-1 lg:gap-x-3`}>
                  <p className="issn_tit w-[70px] font-medium text-[#8D9AAF] lg:w-auto">{it.label}</p>
                  <p>{it.value}</p>
                </div>
                {idx < info.length - 1 && (<span className="hidden lg:block w-[1px] h-[17px] bg-[#E4E8F1]"></span>)}
              </React.Fragment>));
            })()}
          </div>

          <div className="info_wrap pt-6 flex flex-col gap-y-6 lg:gap-y-5 lg:border-b lg:border-b-[#E6E8EC] lg:pb-[26px]">
            <div className="flex items-start gap-x-2 lg:gap-x-3">
              <p className="info_tit shrink-0 w-[100px] text-[14px] leading-[150%] text-[#8D9AAF] font-medium pt-1 lg:w-[110px]">
                Journal Ranking
              </p>
              <div className="journal_lanking flex flex-col gap-[6px] text-[13px] lg:flex-row lg:flex-wrap">
                {(() => {
                  const rankings = arr(record?.journal_ranking);
                  // 정렬: scopus, kci, 나머지(jcr)
                  const sortedRankings = [...rankings].sort((a, b) => {
                    const typeA = String(a?.indicator_type || '').toLowerCase();
                    const typeB = String(b?.indicator_type || '').toLowerCase();
                    const order = { scopus: 1, kci: 2, jcr: 3 };
                    return (order[typeA] || 99) - (order[typeB] || 99);
                  });

                  return sortedRankings.map((rk, idx) => {
                    const type = rk?.indicator_type || '';
                    const name = rk?.name || type.toUpperCase(); // name 필드가 있으면 사용, 없으면 type을 대문자로
                    const year = rk?.year || '';
                    const pct = typeof rk?.percent === 'number' ? rk.percent : rk?.percent;
                    const pctText = (typeof pct === 'number' ? pct.toFixed(2) : pct);
                    const quartile = rk?.quartile || '';
                    const quartileText = quartile ? ` / Q${quartile}` : '';
                    const cls = String(type).toLowerCase();

                    return (
                      <span key={idx} className={`type-${name.toLowerCase()} bg-[#F3F5F9] px-[8px] py-[6px] rounded`}>
                        {name} {year} <strong>Top {pctText}%{quartileText}</strong>
                      </span>
                    );
                  });
                })()}
              </div>
            </div>

            {(() => {
              const registeredDbArray = arr(record?.registered_db);
              const hasRegisteredDb = registeredDbArray.length > 0 && registeredDbArray.some(r => r?.name);
              const hasResourceCount = (record?.resource_count ?? 0) > 0;
              if (!hasRegisteredDb && !hasResourceCount) return null;
              return (
                <div className="flex items-start gap-x-2 lg:gap-x-3">
                  {hasRegisteredDb && (
                    <>
                      <p className="info_tit shrink-0 w-[100px] text-[14px] leading-[150%] text-[#8D9AAF] font-medium lg:w-[110px]">
                        Registered DB
                      </p>
                      <div className="flex flex-wrap items-center gap-y-[10px] gap-x-[2px] text-[14px]">
                        {registeredDbArray.map((r, idx) => {
                          const name = r?.name || '';
                          if (!name) return null;
                          const dbStr = String(name).toLowerCase();
                          const key = dbStr.includes('kci') ? 'kci' : dbStr;
                          return (
                            <span key={`${name}-${idx}`} className={`regist-tpye-${key} px-[5px] py-[2px] rounded`}>
                              {name}
                            </span>
                          );
                        })}
                      </div>
                    </>
                  )}
                  {hasResourceCount && (
                    <div className={`flex flex-wrap items-center gap-y-[10px] gap-x-[2px] text-[14px] ${hasRegisteredDb ? '' : 'ml-[100px] lg:ml-[110px]'}`}>
                      <a href={`/publications/?journal_id[]=${id}`} className="w-full ml-[6px] text-[13px] text-[#B4BCCC] underline underline-offset-2 font-medium lg:w-auto lg:ml-2">
                        PNU Articles ({record.resource_count})
                      </a>
                    </div>
                  )}
                </div>
              );
            })()}

            {(() => {
              const jcr = arr(record?.jcr_category);
              const scopus = arr(record?.scopus_category);
              const kci = arr(record?.kci_category);
              const hasAny = jcr.length || scopus.length || kci.length;
              if (!hasAny) return null;
              return (<div className="not_print flex items-start gap-x-2 lg:gap-x-3">
                <p className="shrink-0 w-[100px] text-[14px] leading-[150%] text-[#8D9AAF] font-medium lg:w-[110px]">Category</p>
                <div className="flex flex-col gap-y-[6px] text-[12px] text-[#6C7687]">
                  {jcr.length > 0 && (<ul className="flex flex-col flex-wrap gap-y-[6px] gap-x-1 lg:flex-row">
                    {jcr.map((c, idx) => (<li key={`jcr-${idx}`} className="w-full px-[10px] py-[6px] bg-[#F3F5F9] rounded-[4px] leading-[100%] lg:w-auto">
                      <strong>WOS</strong> - <span>{c?.category_name || ''}</span>
                    </li>))}
                  </ul>)}
                  {scopus.length > 0 && (<ul className="flex flex-wrap gap-1 lg:flex-row">
                    {scopus.map((c, idx) => (<li key={`scopus-${idx}`} className="w-full px-[10px] py-[6px] bg-[#F3F5F9] rounded-[4px] leading-[100%] lg:w-auto">
                      <strong>Scopus</strong> - <span>{c?.category_name || ''} > {c?.sub_category_name || ''}</span>
                    </li>))}
                  </ul>)}
                  {kci.length > 0 && (<ul className="flex flex-wrap gap-1 lg:flex-row">
                    {kci.filter(c => c?.sub_category_id).map((c, idx) => (
                      <li key={`kci-${idx}`} className="px-[10px] py-[6px] bg-[#F3F5F9] rounded-full">
                        <strong>KCI</strong> - <span>{c?.category_name || ''} > {c?.sub_category_name || ''}</span>
                      </li>
                    ))}
                  </ul>)}
                </div>
              </div>);
            })()}
          </div>

          {showScivalSection && (
            <div className="chart-area not_print journal-print-hide flex flex-col gap-y-4 w-full mt-[45px] lg:mt-[50px] font-[roboto]">
              {/* Row 1: Yearly Publications (100%) */}
              <div className="p-6 border border-[#E4E8F1] rounded-xl lg:p-[32px] w-full">
                <h3 className="mb-6 text-[16px] font-bold leading-[150%] text-center lg:mb-[28px]">
                  Number of Publications by Year
                </h3>
                <YearlyPublicationsChart
                  journalId={null}
                  data={scivalMetrics?.scholarlyOutput ?? null}
                  loading={scivalLoading}
                  error={scivalErr}
                />
              </div>

              {/* Row 2: Collaboration Charts (50% each on md+) */}
              <div className="flex flex-col gap-y-4 gap-x-5 2xl:gap-x-10 md:flex-row w-full">
                <div className="flex-1 p-6 border border-[#E4E8F1] rounded-xl lg:p-[32px]">
                  <h3 className="mb-6 text-[16px] font-bold leading-[150%] text-center">
                    Collaboration Over the Last 10 Years
                  </h3>
                  <CollaborationChart
                    journalId={null}
                    type="collaboration"
                    colors={["#1517FF", "#4F55FF", "#9DA6FF", "#E3DCFF"]}
                    data={scivalMetrics?.collaboration ?? null}
                    loading={scivalLoading}
                    error={scivalErr}
                  />
                </div>

                <div className="flex-1 p-6 border border-[#E4E8F1] rounded-xl lg:p-[32px]">
                  <h3 className="mb-6 text-[16px] font-bold leading-[150%] text-center">
                    Academic-Corporate Collaboration Over the Last 10 Years
                  </h3>
                  <CollaborationChart
                    journalId={null}
                    type="academic_corporate"
                    colors={["#2DB393", "#E4E8F1"]}
                    data={scivalMetrics?.academicCorporate ?? null}
                    loading={scivalLoading}
                    error={scivalErr}
                  />
                </div>
              </div>
            </div>
          )}

          {/*<AimsAndScope />*/}

          <div className="journal-indexing-info mt-[60px] lg:mt-[80px]">
            <h2 className="text-[18px] font-bold mb-5 lg:text-[20px] lg:mb-6">
              Journal Indexing Information
            </h2>

            <ul className="not_print flex flex-col gap-y-[8px] mb-4 p-6 pl-[34px] bg-[#F3F5F9] text-[14px] text-[#6C7687] font-medium rounded-xl list-disc lg:mb-6">
              <li>
                SCI급 저널 : Web of Science 핵심컬렉션 DB중 SCIE, SSCI, AHCI 저널을 통칭함. (ESCI 제외)
              </li>
              <li>
                ESCI는 SCI급 저널의 후보군으로 판단되며, ESCI의 등재일은 정확하지 않으며 업데이트 중.
              </li>
            </ul>

            <JournalIndexingInfo dbRegister={record?.db_register} />
          </div>

          <div className="journal-citation-metrics mt-[50px] lg:mt-[80px]">
            <h2 className="text-[18px] font-bold mb-5 lg:text-[20px] lg:mb-6">
              Journal Citation Metrics
            </h2>

            <ul className="not_print journal_citation_info flex flex-col gap-y-[8px] mb-4 p-6 pl-[34px] bg-[#F3F5F9] text-[14px] text-[#6C7687] font-medium rounded-xl list-disc lg:mb-6" >
              {!rmVars.isLogin && (
                <li>
                  <img src={`${rmVars.themeUrl}images/warning-ico-6C7687.png`} className="not_print absolute top-[25px] left-6 w-[17px]" alt="error" />
                  <p className="font-bold">Journal metrics are available after <a href={getLoginUrlWithRedirect()} className="text-[#4B4DFF]">login</a>.</p>
                  <div>If you are not logged in, some values will be masked for access control purposes.</div>
                </li>
              )}
              <li>
                For more information, please refer to the "Extra Link" in the side menu.
              </li>
            </ul>

            <JournalCitationMetrics journalId={id} indicatorData={indicator} />
          </div>

          <div className="not_print journal-print-hide mt-[60px] lg:mt-[80px]">
            <h2 className="text-[18px] font-bold mb-5 lg:text-[20px] lg:mb-6">
              Impact Factor Trend
            </h2>

            <ImpactFactorTrendChart
              height={340}
              hideXAxisLabels={false}
              journalId={id}
            />
          </div>

          <div className="mt-[60px] lg:mt-[80px]">
            <h2 className="text-[18px] font-bold mb-5 lg:text-[20px] lg:mb-6">
              Category Ranking Information
            </h2>

            <CategoryRankingTable rows={indicator?.category_ranking} />

          </div>

          {arr(record?.journal_dept_graph).length > 0 && (
            <div className="mt-[60px] lg:mt-[80px]">
              <h2 className="text-[18px] font-bold mb-5 lg:text-[20px] lg:mb-6">
                Major Publishing Departments
              </h2>

              <MajorPublishingDepartments
                data={arr(record?.journal_dept_graph)}
                maxItems={5}
              />
            </div>
          )}

          <JournalMajorAuthors journalId={id} />

          <div className={`journal-print-hide ${pubRows.length === 0 ? 'hidden ' : 'lg:block '}mt-[60px] lg:mt-[80px]`}>
            <h2 className="text-[18px] font-bold mb-5 lg:text-[20px] lg:mb-6">
              Publications by PNU Researchers
            </h2>

            <ul className="flex flex-col gap-4">
              {pubRows.map((it, idx) => (<PublicationCard key={it.id || it.resource_id || idx} item={it} showAltmetric={showAltmetric} />))}
            </ul>

            {pubRows.length > 0 && (
              <div className="text-center mt-10">
                <button
                  type="button"
                  onClick={handleMoreResources}
                  className="inline-flex items-center justify-center gap-1 py-3 px-8 bg-[#F3F5F9] border border-[#E4E8F1] rounded-xl"
                >
                  <span className="text-[14px] text-[#6C7687]">More</span>
                  <img
                    src={`${rmVars.themeUrl}images/launch-ico-6C7687.png`}
                    className="w-[16px]"
                    alt="link"
                  />
                </button>
              </div>
            )}
          </div>

        </div>


      </div>

      <JournalRightBox record={record} />
    </div>)
}
